import { ReactNode } from "react";

const statusOf = (error: unknown) => (error as { response?: { status?: number } } | null)?.response?.status;

interface ResultCountProps {
  loading: boolean;
  count?: number;
  // e.g. "rezervime"; the count is printed before it.
  noun: string;
  // Anything to show at the right of the line, such as a legend button.
  aside?: ReactNode;
}

// Says whether the filters narrowed anything; blank while loading rather than a stale count.
export const ResultCount = ({ loading, count, noun, aside }: ResultCountProps) => (
  <div className="flex min-h-[2rem] items-center justify-between gap-3 px-1">
    <p className="text-sm text-neutral" aria-live="polite">
      {!loading && !!count && `${count.toLocaleString("sq-AL")} ${noun}`}
    </p>
    {aside}
  </div>
);

interface FilteredListStateProps {
  loading: boolean;
  error: unknown;
  isEmpty: boolean;
  emptyText: string;
  hasFilters: boolean;
  onClear: () => void;
  onRetry: () => void;
  // The ticket list's endpoint answers 404 when nothing matches, so there a 404 is "no results".
  notFoundMeansEmpty?: boolean;
}

// What a filtered list shows when it has no rows: a failure is said as such, with a retry, so an
// outage never reads as "nothing matches these filters".
const FilteredListState = ({
  loading, error, isEmpty, emptyText, hasFilters, onClear, onRetry, notFoundMeansEmpty
}: FilteredListStateProps) => {
  if (loading) return null;

  const failed = !!error && !(notFoundMeansEmpty && statusOf(error) === 404);
  const buttonClass = "rounded-lg border border-gray-200 px-3 py-1.5 text-black hover:bg-gray-50";

  if (failed) {
    return (
      <div role="alert" className="flex flex-col items-center gap-3 py-10 text-sm text-neutral">
        <p>Rezervimet nuk u ngarkuan. Kontrollo lidhjen dhe provo përsëri.</p>
        <button type="button" onClick={onRetry} className={buttonClass}>
          Provo përsëri
        </button>
      </div>
    );
  }

  if (!isEmpty) return null;
  return (
    <div className="flex flex-col items-center gap-3 py-10 text-sm text-neutral">
      <p>{emptyText}</p>
      {hasFilters && (
        <button type="button" onClick={onClear} className={buttonClass}>
          Pastro filtrat
        </button>
      )}
    </div>
  );
};

export default FilteredListState;
